<template>
  <div class="edit-work-info-wrapper">
    <div class="edit-work-info-container">
      <div class="route-name">{{ $t(`message.sideMenu.${this.$route.name}`) }}</div>
      <div class="back-block" @click="$router.push({ name: 'change-work-info' })">
        <img
          src="../../../../public/custom/images/back-arrow.svg"
          class="back-img"
          alt="Back arrow"
        />
        <span class="back-btn-text">{{ $t('message.loanPage.back') }}</span>
      </div>
      <template v-if="isReady">
        <div class="sections-row">
        <div class="info-section">
          <div class="section-title">{{ $t('message.editWorkInfoPage.workTitle') }}</div>
          <div class="work-info-form">
            <div class="form-field">
              <div class="form-label">{{ $t('message.editWorkInfoPage.fields.employmentType') }}</div>
              <div v-if="workUnresolved.employment_type" class="form-current">
                {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ workUnresolved.employment_type }}
              </div>
              <base-select
                :default-value="workForm.employment_type"
                :dictionary-path="'message.signUp.jobBlock.typeOfEmployment.list.'"
                :options-list="employmentTypeOptions"
                :name="'employment_type'"
                v-model="workForm.employment_type"
              />
            </div>
            <div class="form-field">
              <div class="form-label">{{ $t('message.editWorkInfoPage.fields.scopeOfEmployment') }}</div>
              <div v-if="workUnresolved.scope_of_employment_type" class="form-current">
                {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ workUnresolved.scope_of_employment_type }}
              </div>
              <base-select
                :default-value="workForm.scope_of_employment_type"
                :dictionary-path="'message.signUp.jobBlock.fieldOfEmployment.list.'"
                :options-list="CONSTANTS.SCOPE_OF_EMPLOYMENT_TYPE_LIST"
                :name="'scope_of_employment_type'"
                :disabled="isEmploymentWorkFieldsDisabled"
                v-model="workForm.scope_of_employment_type"
              />
            </div>
            <div class="form-field">
              <div class="form-label">{{ $t('message.editWorkInfoPage.fields.jobPosition') }}</div>
              <div v-if="workUnresolved.job_position_type" class="form-current">
                {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ workUnresolved.job_position_type }}
              </div>
              <base-select
                :default-value="workForm.job_position_type"
                :dictionary-path="'message.signUp.jobBlock.post.list.'"
                :options-list="CONSTANTS.POST_TYPE_LIST"
                :name="'job_position_type'"
                :disabled="isEmploymentWorkFieldsDisabled"
                v-model="workForm.job_position_type"
              />
            </div>
            <div v-if="isOtherJobPositionType" class="form-field">
              <div class="form-label">{{ $t('message.editWorkInfoPage.fields.jobPositionName') }}</div>
              <base-text-input
                :default-value="workForm.job_position_name"
                :name="'job_position_name'"
                :is-has-error-block="false"
                :disabled="isEmploymentWorkFieldsDisabled"
                v-model="workForm.job_position_name"
              />
            </div>
            <div class="form-field">
              <div class="form-label">{{ $t('message.editWorkInfoPage.fields.organizationName') }}</div>
              <base-text-input
                :default-value="workForm.organization_name"
                :name="'organization_name'"
                :is-has-error-block="false"
                :disabled="isEmploymentWorkFieldsDisabled"
                v-model="workForm.organization_name"
              />
            </div>
            <div class="form-field">
              <div class="form-label">{{ $t('message.editWorkInfoPage.fields.workExperience') }}</div>
              <base-text-input
                :default-value="workForm.workExperience"
                :name="'workExperience'"
                :is-has-error-block="false"
                :mask-pattern="'####'"
                :disabled="isEmploymentWorkFieldsDisabled"
                v-model="workForm.workExperience"
              />
            </div>
          </div>
        </div>
        <div class="info-section">
          <div class="section-title">{{ $t('message.editWorkInfoPage.extraTitle') }}</div>
          <div class="work-info-form">
            <div class="form-field">
              <div class="form-label">{{ $t('message.changeWorkInfoPage.extraFields.education') }}</div>
              <div v-if="extraUnresolved.education" class="form-current">
                {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ extraUnresolved.education }}
              </div>
              <base-select
                :default-value="extraForm.education"
                :dictionary-path="'message.signUp.extraBlock.education.list.'"
                :options-list="educationOptions"
                :name="'education'"
                v-model="extraForm.education"
              />
            </div>
            <div class="form-field">
              <div class="form-label">{{ $t('message.changeWorkInfoPage.extraFields.driversLicense') }}</div>
              <div v-if="extraUnresolved.drivers_license" class="form-current">
                {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ extraUnresolved.drivers_license }}
              </div>
              <base-select
                :default-value="extraForm.drivers_license"
                :dictionary-path="'message.editWorkInfoPage.yesNo.list.'"
                :options-list="yesNoOptions"
                :name="'drivers_license'"
                v-model="extraForm.drivers_license"
              />
            </div>
            <div class="form-field">
              <div class="form-label">{{ $t('message.changeWorkInfoPage.extraFields.carOwner') }}</div>
              <div v-if="extraUnresolved.car_owner" class="form-current">
                {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ extraUnresolved.car_owner }}
              </div>
              <base-select
                :default-value="extraForm.car_owner"
                :dictionary-path="'message.editWorkInfoPage.yesNo.list.'"
                :options-list="yesNoOptions"
                :name="'car_owner'"
                v-model="extraForm.car_owner"
              />
            </div>
            <div v-if="isCarOwner" class="form-field documents-upload-field">
              <SignUpDocumentsUploader
                v-model="car_passport_files"
                :title="$t('message.signUp.extraBlock.car.technicalPassport.title')"
                :hint="$t('message.signUp.extraBlock.car.technicalPassport.hint')"
                :attach-btn="$t('message.signUp.extraBlock.car.technicalPassport.attachBtn')"
                :max-files="4"
              />
            </div>
            <div class="form-field">
              <div class="form-label">{{ $t('message.changeWorkInfoPage.extraFields.realEstateOwner') }}</div>
              <div v-if="extraUnresolved.real_estate_owner" class="form-current">
                {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ extraUnresolved.real_estate_owner }}
              </div>
              <base-select
                :default-value="extraForm.real_estate_owner"
                :dictionary-path="'message.editWorkInfoPage.yesNo.list.'"
                :options-list="yesNoOptions"
                :name="'real_estate_owner'"
                v-model="extraForm.real_estate_owner"
              />
            </div>
            <div v-if="isRealEstateOwner" class="form-field documents-upload-field">
              <SignUpDocumentsUploader
                v-model="real_estate_files"
                :title="$t('message.signUp.extraBlock.realEstate.registrationCertificate.title')"
                :hint="$t('message.signUp.extraBlock.realEstate.registrationCertificate.hint')"
                :attach-btn="$t('message.signUp.extraBlock.realEstate.registrationCertificate.attachBtn')"
                :max-files="5"
              />
            </div>
            <div class="form-field">
              <div class="form-label">{{ $t('message.changeWorkInfoPage.extraFields.familyStatus') }}</div>
              <div v-if="extraUnresolved.family_status" class="form-current">
                {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ extraUnresolved.family_status }}
              </div>
              <base-select
                :default-value="extraForm.family_status"
                :dictionary-path="'message.signUp.extraBlock.familyStatus.list.'"
                :options-list="familyStatusOptions"
                :name="'family_status'"
                v-model="extraForm.family_status"
              />
            </div>
            <div class="form-field">
              <div class="form-label">{{ $t('message.changeWorkInfoPage.extraFields.dependensNumber') }}</div>
              <div v-if="extraUnresolved.dependens_number" class="form-current">
                {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ extraUnresolved.dependens_number }}
              </div>
              <base-text-input
                :default-value="extraForm.dependens_number"
                :name="'dependens_number'"
                :is-has-error-block="false"
                v-model="extraForm.dependens_number"
              />
            </div>
            <div class="form-field">
              <div class="form-label">{{ $t('message.changeWorkInfoPage.extraFields.childExpenses') }}</div>
              <div v-if="extraUnresolved.child_expenses" class="form-current">
                {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ extraUnresolved.child_expenses }}
              </div>
              <base-select
                :default-value="extraForm.child_expenses"
                :dictionary-path="'message.editWorkInfoPage.yesNo.list.'"
                :options-list="yesNoOptions"
                :name="'child_expenses'"
                v-model="extraForm.child_expenses"
              />
            </div>
            <div class="form-field">
              <div class="form-label">{{ $t('message.changeWorkInfoPage.extraFields.totalIncome') }}</div>
              <div v-if="extraUnresolved.total_income" class="form-current">
                {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ extraUnresolved.total_income }}
              </div>
              <base-text-input
                :default-value="extraForm.total_income"
                :name="'total_income'"
                :is-has-error-block="false"
                :mask-pattern="'######'"
                :disabled="isTotalIncomeDisabled"
                v-model="extraForm.total_income"
              />
            </div>
          </div>
        </div>
        </div>
        <div class="form-actions">
          <div class="form-btn-container">
            <base-button
              :text="$t('message.editWorkInfoPage.btnSave')"
              :disabled="isSaveDisabled || isSaveBtnLoading"
              :is-loading="isSaveBtnLoading"
              @click="saveWorkInfo()"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseButton from '../../../components/base/BaseButton.vue';
import BaseSelect from '../../../components/base/BaseSelect.vue';
import BaseTextInput from '../../../components/base/BaseTextInput.vue';
import SignUpDocumentsUploader from '../../../components/common/SignUpDocumentsUploader.vue';
import CONSTANTS from '../../../constants/constants';
import {
  buildExtraUserInfoEditForm,
  buildExtraUserInfoSavePayload,
  getExtraUserInfoEducationOptions,
  getExtraUserInfoFamilyStatusOptions,
  getExtraUserInfoYesNoOptions
} from '../../../helpers/extraUserInfo';
import {
  buildWorkInfoEditForm,
  getDefaultEmploymentWorkFields,
  getDisabledEmploymentWorkFields,
  getWorkInfoEmploymentTypeOptions,
  isCustomJobPositionType,
  isEmploymentTypeWithDisabledWorkFields
} from '../../../helpers/workInfo';

export default {
  name: 'EditWorkInfoBorrower',
  inject: ['$httpService'],
  components: {
    BaseButton,
    BaseSelect,
    BaseTextInput,
    SignUpDocumentsUploader
  },
  data() {
    return {
      CONSTANTS,
      employmentTypeOptions: getWorkInfoEmploymentTypeOptions(),
      educationOptions: getExtraUserInfoEducationOptions(),
      familyStatusOptions: getExtraUserInfoFamilyStatusOptions(),
      yesNoOptions: getExtraUserInfoYesNoOptions(),
      isReady: false,
      isSaveBtnLoading: false,
      car_passport_files: [],
      real_estate_files: [],
      workForm: {
        employment_type: '',
        scope_of_employment_type: '',
        organization_name: '',
        job_position_type: '',
        job_position_name: '',
        workExperience: ''
      },
      workUnresolved: {
        employment_type: null,
        scope_of_employment_type: null,
        job_position_type: null
      },
      extraForm: {
        education: '',
        drivers_license: '',
        car_owner: '',
        real_estate_owner: '',
        family_status: '',
        dependens_number: '',
        child_expenses: '',
        total_income: ''
      },
      extraUnresolved: {
        education: null,
        drivers_license: null,
        car_owner: null,
        real_estate_owner: null,
        family_status: null,
        dependens_number: null,
        child_expenses: null,
        total_income: null
      }
    };
  },
  async created() {
    await this.$store.dispatch('getProfile');

    if (this.profile?.role !== 'borrower' || !this.profile?.expiredCreditRegisterAgreement) {
      this.$router.replace({ name: 'change-work-info' });
      return;
    }

    this.setFormData();
  },
  computed: {
    ...mapGetters(['profile']),
    isOtherJobPositionType() {
      return isCustomJobPositionType(this.workForm.job_position_type);
    },
    isEmploymentWorkFieldsDisabled() {
      return isEmploymentTypeWithDisabledWorkFields(this.workForm.employment_type);
    },
    isTotalIncomeDisabled() {
      return this.workForm.employment_type === '3';
    },
    isCarOwner() {
      return this.extraForm.car_owner === 'true';
    },
    isRealEstateOwner() {
      return this.extraForm.real_estate_owner === 'true';
    },
    isCarPassportIncorrect() {
      if (!this.isCarOwner) {
        return false;
      }

      return this.car_passport_files.length < 2;
    },
    isRealEstateDocumentsIncorrect() {
      if (!this.isRealEstateOwner) {
        return false;
      }

      return this.real_estate_files.length < 1;
    },
    isWorkSaveDisabled() {
      if (this.isEmploymentWorkFieldsDisabled) {
        return !this.workForm.employment_type;
      }

      return !this.workForm.employment_type
        || !this.workForm.scope_of_employment_type
        || !this.workForm.job_position_type
        || (this.isOtherJobPositionType && !String(this.workForm.job_position_name || '').trim())
        || !String(this.workForm.organization_name || '').trim()
        || !String(this.workForm.workExperience || '').trim();
    },
    isExtraSaveDisabled() {
      return !this.extraForm.education
        || !this.extraForm.drivers_license
        || !this.extraForm.car_owner
        || !this.extraForm.real_estate_owner
        || !this.extraForm.family_status
        || String(this.extraForm.dependens_number ?? '').trim() === ''
        || !this.extraForm.child_expenses
        || !String(this.extraForm.total_income || '').trim()
        || this.isCarPassportIncorrect
        || this.isRealEstateDocumentsIncorrect;
    },
    isSaveDisabled() {
      return this.isWorkSaveDisabled || this.isExtraSaveDisabled;
    }
  },
  watch: {
    'workForm.employment_type'(value, previousValue) {
      if (!this.isReady) {
        return;
      }

      if (value === '3') {
        this.setDisabledTotalIncome();
      } else if (previousValue === '3') {
        this.setDefaultTotalIncome();
      }

      if (isEmploymentTypeWithDisabledWorkFields(value)) {
        this.setDisabledEmploymentWorkFields();
        return;
      }

      if (isEmploymentTypeWithDisabledWorkFields(previousValue)) {
        this.setDefaultEmploymentWorkFields();
      }
    },
    'extraForm.car_owner'(value) {
      if (!this.isReady || value === 'true') {
        return;
      }

      this.car_passport_files = [];
    },
    'extraForm.real_estate_owner'(value) {
      if (!this.isReady || value === 'true') {
        return;
      }

      this.real_estate_files = [];
    }
  },
  methods: {
    setFormData() {
      const workFormState = buildWorkInfoEditForm(this.profile?.work_info);
      const extraFormState = buildExtraUserInfoEditForm(
        this.profile?.extra_user_info,
        this.$t.bind(this)
      );

      this.workForm = {
        employment_type: workFormState.employment_type,
        scope_of_employment_type: workFormState.scope_of_employment_type,
        organization_name: workFormState.organization_name,
        job_position_type: workFormState.job_position_type,
        job_position_name: workFormState.job_position_name,
        workExperience: workFormState.workExperience
      };
      this.workUnresolved = workFormState.unresolved;

      this.extraForm = {
        education: extraFormState.education,
        drivers_license: extraFormState.drivers_license,
        car_owner: extraFormState.car_owner,
        real_estate_owner: extraFormState.real_estate_owner,
        family_status: extraFormState.family_status,
        dependens_number: extraFormState.dependens_number,
        child_expenses: extraFormState.child_expenses,
        total_income: extraFormState.total_income
      };
      this.extraUnresolved = extraFormState.unresolved;

      if (isEmploymentTypeWithDisabledWorkFields(this.workForm.employment_type)) {
        this.setDisabledEmploymentWorkFields();
      }

      if (this.workForm.employment_type === '3') {
        this.setDisabledTotalIncome();
      }

      this.isReady = true;
    },
    setDisabledEmploymentWorkFields() {
      const disabledFields = getDisabledEmploymentWorkFields();

      this.workForm.scope_of_employment_type = disabledFields.scope_of_employment_type;
      this.workForm.organization_name = disabledFields.organization_name;
      this.workForm.job_position_type = disabledFields.job_position_type;
      this.workForm.job_position_name = disabledFields.job_position_name;
      this.workForm.workExperience = disabledFields.workExperience;
    },
    setDefaultEmploymentWorkFields() {
      const defaultFields = getDefaultEmploymentWorkFields();

      this.workForm.scope_of_employment_type = defaultFields.scope_of_employment_type;
      this.workForm.organization_name = defaultFields.organization_name;
      this.workForm.job_position_type = defaultFields.job_position_type;
      this.workForm.job_position_name = defaultFields.job_position_name;
      this.workForm.workExperience = '';
    },
    setDisabledTotalIncome() {
      this.extraForm.total_income = '0';
    },
    setDefaultTotalIncome() {
      this.extraForm.total_income = '';
    },
    async saveWorkInfo() {
      if (this.isSaveDisabled || this.isSaveBtnLoading) {
        return;
      }

      this.isSaveBtnLoading = true;

      try {
        const workInfo = {
          employment_type: this.workForm.employment_type,
          scope_of_employment_type: this.workForm.scope_of_employment_type,
          organization_name: this.workForm.organization_name,
          job_position_type: this.workForm.job_position_type,
          workExperience: this.workForm.workExperience
        };

        if (isCustomJobPositionType(this.workForm.job_position_type)) {
          workInfo.job_position_name = this.workForm.job_position_name;
        }

        const extraUserInfo = buildExtraUserInfoSavePayload(this.extraForm);
        const response = await this.$httpService.auth.editBorrowerWorkInfo(
          workInfo,
          extraUserInfo,
          {
            car_passport_files: this.car_passport_files,
            real_estate_files: this.real_estate_files
          }
        );

        if (!response) {
          throw new Error('Edit borrower work info failed');
        }

        this.$router.push({ name: 'change-work-info' });
      } catch (error) {
        this.isSaveBtnLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.edit-work-info-wrapper {
  @include page-wrapper-with-header-and-footer;

  padding-top: 20px;
  padding-bottom: 40px;
  box-sizing: border-box;
}

.edit-work-info-container {
  width: 95%;
  min-height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
}

.back-block {
  width: fit-content;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.back-img {
  margin-right: 4px;
  height: 18px;
  width: 18px;
}

.back-btn-text {
  font-weight: 400;
  font-size: 16px;
  line-height: 135%;
  color: #878787;
}

.route-name {
  @include route-name;
  margin-bottom: 15px;
}

.sections-row {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
}

.info-section {
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  border-radius: 12px;
  background-color: #FFFFFF;
  border: 1px solid #ECEDEE;
  box-shadow: 0 2px 8px rgba(1, 39, 59, 0.06);
}

.section-title {
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: #01273B;
}

.work-info-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-field {
  width: 100%;
}

.documents-upload-field {
  margin-top: 4px;
  margin-bottom: 8px;
}

.documents-upload-field ::v-deep .documents-uploader-header {
  flex-wrap: wrap;
}

.documents-upload-field ::v-deep .documents-uploader-title {
  font-size: 14px;
  line-height: 22px;
}

.form-label {
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: #7A7A7A;
}

.form-field ::v-deep .input-block,
.form-field ::v-deep .select-block {
  height: 40px;
  min-height: 40px;
  border-radius: 6px;
}

.form-field ::v-deep .input {
  height: 38px;
  min-height: 38px;
  padding: 8px;
  font-size: 14px;
  line-height: 24px;
  color: #01273B;
}

.form-field ::v-deep .select-block .input {
  height: 100%;
  min-height: 100%;
}

.form-field ::v-deep .select-block.disabled {
  background-color: rgba(239, 239, 239, 0.3);
  cursor: default;
}

.form-field ::v-deep .input:disabled {
  background-color: rgba(239, 239, 239, 0.3);
  cursor: default;
}

.form-field ::v-deep .error-block {
  height: 16px;
  margin-top: 2px;
  font-size: 12px;
  line-height: 16px;
}

.form-current {
  margin-bottom: 8px;
  padding: 10px 12px;
  box-sizing: border-box;
  border: 1px solid #ECEDEE;
  border-radius: 8px;
  background: #F8FAF9;
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: #333333;
}

.form-actions {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}

.form-btn-container {
  width: 200px;
  height: 40px;
}

.form-btn-container ::v-deep .button {
  min-height: 40px;
  max-height: 40px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 24px;
}

@media screen and (max-width: 1155px) {
  .edit-work-info-wrapper {
    padding-top: 40px;
  }

  .edit-work-info-container {
    @include base-page-container;

    align-items: flex-start;
    padding-top: 0;
  }

  .sections-row {
    flex-direction: column;
  }
}

@media screen and (max-width: 600px) {
  .form-btn-container {
    width: 100%;
  }
}
</style>
